import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import NotificationBell from '../../components/common/NotificationBell';
import UserAvatar from '../../components/common/UserAvatar';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import AccountStatusBanner from '../../components/common/AccountStatusBanner';
import BannerSlider from '../../components/common/BannerSlider';
import { 
  Power, MapPin, Clock, Star, CheckCircle, XCircle, AlertCircle, User, Briefcase, Wallet
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProviderHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/providers/${user.id}`),
        axios.get(`${API}/bookings?status=pending`)
      ]);
      setProfile(profileRes.data);
      setPendingBookings(bookingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) fetchData();
  }, [user?.id, fetchData]);

  // WebSocket for real-time booking notifications
  useEffect(() => {
    if (!user?.id) return;
    
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.id}`);
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_booking') {
        toast.info('New booking request!');
        fetchData();
      }
      if (data.type === 'verification_update') {
        toast.success(`Verification ${data.status}!`);
        fetchData();
      }
    };
    
    return () => ws.close();
  }, [user?.id, fetchData]);

  const handleToggleOnline = async () => {
    setIsToggling(true);
    try {
      const response = await api.toggleOnline();
      setProfile(prev => ({ ...prev, is_online: response.data.is_online }));
      toast.success(response.data.is_online ? 'You are now online!' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setIsToggling(false);
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      await api.updateBookingStatus(bookingId, { status: 'accepted' });
      toast.success('Booking accepted!');
      setSelectedBooking(null);
      fetchData();
      navigate(`/booking/${bookingId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept booking');
    }
  };

  const handleDeclineBooking = async (bookingId) => {
    try {
      await api.updateBookingStatus(bookingId, { status: 'cancelled', notes: 'Declined by provider' });
      toast.info('Booking declined');
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to decline booking');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const isVerified = profile?.verification_status === 'verified';
  const isPending = profile?.verification_status === 'pending';

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-8 px-4 rounded-b-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-slate-400 text-sm">Provider Dashboard</p>
            <h1 className="text-white font-bold text-xl font-[Poppins]">{user?.name || 'Provider'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button 
              className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
              onClick={() => navigate('/provider/profile')}
            >
              <UserAvatar 
                src={user?.profile_image} 
                name={user?.name} 
                role="provider"
                size="md"
              />
            </button>
          </div>
        </div>

        {/* Online Toggle */}
        {isVerified && (
          <div className="bg-slate-800 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                profile?.is_online ? 'bg-green-500' : 'bg-slate-600'
              }`}>
                <Power className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">
                  {profile?.is_online ? 'Online' : 'Offline'}
                </p>
                <p className="text-slate-400 text-sm">
                  {profile?.is_online ? 'Accepting bookings' : 'Not accepting bookings'}
                </p>
              </div>
            </div>
            <Switch
              checked={profile?.is_online || false}
              onCheckedChange={handleToggleOnline}
              disabled={isToggling}
              data-testid="online-toggle"
            />
          </div>
        )}
      </header>

      <main className="px-4 -mt-4">
        {/* Account Status Warnings (Wallet, Identity) */}
        <AccountStatusBanner />
        
        {/* Promotional Banner Slider */}
        <div className="mb-4">
          <BannerSlider audience="provider" />
        </div>
        
        {/* Service Area Alert - Show if no location set and provider is verified */}
        {isVerified && (!profile?.service_latitude || !profile?.service_longitude) && (
          <div 
            className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-6 cursor-pointer hover:bg-amber-100 transition-colors shadow-md"
            onClick={() => navigate('/provider/profile')}
            data-testid="service-area-alert"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                <AlertCircle className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-amber-900">Location Not Set - Action Required</p>
                <p className="text-sm text-amber-700 mt-0.5">
                  You must set your service location to be visible to customers. Without a location, customers cannot find you in search results.
                </p>
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  Tap here to set your location now
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Verification Status */}
        {!isVerified && (
          <div className={`rounded-2xl p-4 mb-6 ${
            isPending ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {isPending ? (
                <Clock className="w-6 h-6 text-amber-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              )}
              <div>
                <p className={`font-semibold ${isPending ? 'text-amber-900' : 'text-red-900'}`}>
                  {isPending ? 'Verification Pending' : 'Complete Verification'}
                </p>
                <p className={`text-sm mt-1 ${isPending ? 'text-amber-700' : 'text-red-700'}`}>
                  {isPending 
                    ? 'Your documents are being reviewed. This usually takes 24-48 hours.'
                    : 'Upload your ID to verify your identity and start accepting jobs.'}
                </p>
                {!isPending && (
                  <Button 
                    size="sm" 
                    className="mt-3 bg-slate-900"
                    onClick={() => navigate('/provider/profile')}
                    data-testid="complete-verification-btn"
                  >
                    Complete Verification
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-amber-500" />
              <span className="text-slate-500 text-sm">Rating</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{profile?.rating || '0.0'}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-blue-500" />
              <span className="text-slate-500 text-sm">Jobs</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{profile?.total_jobs || 0}</p>
          </div>
        </div>

        {/* Pending Bookings */}
        {isVerified && profile?.is_online && (
          <section className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 font-[Poppins] mb-4">
              Incoming Requests ({pendingBookings.length})
            </h2>
            
            {pendingBookings.length > 0 ? (
              <div className="space-y-3">
                {pendingBookings.map(booking => (
                  <button
                    key={booking.id}
                    className="w-full bg-white rounded-2xl p-4 shadow-sm border-l-4 border-l-amber-500 text-left hover:shadow-md transition-shadow"
                    onClick={() => setSelectedBooking(booking)}
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-slate-900">New Booking</p>
                        <p className="text-sm text-slate-500">{booking.scheduled_date} • {booking.scheduled_time}</p>
                      </div>
                      <span className="text-lg font-bold text-amber-600">₱{booking.provider_earnings}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <p className="text-sm text-slate-600 truncate">{booking.address?.city}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-stone-50 rounded-2xl">
                <Briefcase className="w-12 h-12 text-slate-200 mx-auto mb-2" />
                <p className="text-slate-500">No pending requests</p>
                <p className="text-sm text-slate-400">New bookings will appear here</p>
              </div>
            )}
          </section>
        )}

        {/* Quick Actions */}
        <section className="mb-24">
          <h2 className="text-lg font-bold text-slate-900 font-[Poppins] mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <button 
              className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3"
              onClick={() => navigate('/provider/jobs')}
              data-testid="my-jobs-btn"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">My Jobs</p>
                <p className="text-xs text-slate-500">View all jobs</p>
              </div>
            </button>
            <button 
              className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3"
              onClick={() => navigate('/provider/earnings')}
              data-testid="earnings-btn"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Earnings</p>
                <p className="text-xs text-slate-500">View & withdraw</p>
              </div>
            </button>
          </div>
        </section>
      </main>

      {/* Bottom Navigation */}
      <ProviderBottomNav />

      {/* Booking Request Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Booking Request</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-sm text-amber-800">You'll earn</p>
                <p className="text-3xl font-bold text-amber-600">₱{selectedBooking.provider_earnings}</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-900">Schedule</p>
                    <p className="text-sm text-slate-500">
                      {selectedBooking.scheduled_date} at {selectedBooking.scheduled_time}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="font-medium text-slate-900">Location</p>
                    <p className="text-sm text-slate-500">{selectedBooking.address?.full_address}</p>
                    <p className="text-xs text-slate-400">{selectedBooking.address?.city}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 h-12"
                  onClick={() => handleDeclineBooking(selectedBooking.id)}
                  data-testid="decline-booking-btn"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Decline
                </Button>
                <Button 
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700"
                  onClick={() => handleAcceptBooking(selectedBooking.id)}
                  data-testid="accept-booking-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Accept
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
